// AppShellHost — the mount of the `library` module (the Bibliotek). The shell
// edge for the module under the client runtime (app/runtime.ts):
//  - `useLibraryMount` mounts the module ONCE in the shell's runtime (the one
//    chat lives in), so the module outlives the Search tab (marked row, the
//    opened item's content) and hears the world change (`Signal.WorldChanged`
//    re-reads the open item). Its out-Signals (`FrameRequested`) reach the
//    frame through the chat route table (useChatSignalRoutes).
//  - `useLibrary` projects the Model into React where the Library renders, so
//    a keystroke re-renders the Library, not the whole shell.

import { useState, useSyncExternalStore } from "react";
import { DEFAULT_SORT } from "../../components/Search/searchModel.js";
import {
    type LibraryDeps,
    type LibraryModel,
    type LibraryMsg,
    mkLibraryModule,
} from "../../modules/library.js";
import { readLibrarySort, writeLibrarySort } from "../librarySortPreference.js";
import type { Runtime } from "../runtime.js";

export type LibraryMount = {
    readonly runtime: Runtime;
    readonly getModel: () => LibraryModel;
    readonly send: (msg: LibraryMsg) => void;
};

export const useLibraryMount = (
    runtime: Runtime,
    ports: LibraryDeps["ports"],
): LibraryMount => {
    const [mount] = useState((): LibraryMount => {
        const library = runtime.mount(
            mkLibraryModule(
                { ports, rememberSort: writeLibrarySort },
                readLibrarySort() ?? DEFAULT_SORT,
            ),
        );
        return {
            runtime,
            getModel: library.getModel,
            // A refusal (back to the list while none is open, …) is silent,
            // visible only in dev.
            send: (msg) => {
                const r = library.dispatch(msg);
                if (r._tag === "Result.Err" && import.meta.env.DEV)
                    console.debug("[library] refused", r.error.type, msg.type);
            },
        };
    });
    return mount;
};

export const useLibrary = (mount: LibraryMount): LibraryModel =>
    useSyncExternalStore(mount.runtime.subscribe, mount.getModel);
